import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'src/components/layout';
import {
  convertSecondsToHMS,
  getTimestampOf,
} from 'src/utils/helpers';
import * as ActionTypes from 'src/store/actions';
import { Row } from 'src/components/Row';
import { Card } from 'src/components/Card';
import { Col } from 'src/components/Col';
import { Table } from 'src/components/Table';
import { groupBy, orderBy } from 'lodash';
import { MdRefresh } from 'react-icons/md';
import { Tooltip } from 'src/components/Tooltip';

export default function Sky() {
  const dispatch = useDispatch();
  const { startDate, endDate, lastUpdate } = useSelector((state: any) => state.dateTime);
  const { genderFilter, ageFilter } = useSelector((state: any) => state.filter);
  const { dwellAnalysis, dwellAnalysisLoading } = useSelector((state: any) => state.areas);
  const { storeGraphs, storeGraphs_loading } = useSelector((state: any) => state.graphs);
  const { allPolygonMetrics, allPolygonMetricsLoading } = useSelector((state: any) => state.polygons);
  const { stores } = useSelector((state: any) => state.stores);
  const [polygonMetricsData, setPolygonMetricsData] = useState({} as any);
  const startTimeTimestamp = Math.trunc(getTimestampOf(new Date(startDate)));
  const endTimeTimestamp = Math.trunc(getTimestampOf(new Date(endDate)));

  useEffect(() => {
    dispatch(
      ActionTypes.getAllPolygonMetrics.request({
        startDate: startTimeTimestamp.toString(),
        endDate: endTimeTimestamp.toString(),
      }),
    );
    
    if (stores.length === 0) {
      dispatch(ActionTypes.getAllStores.request({}));
    }
  }, [startTimeTimestamp, endTimeTimestamp, genderFilter, ageFilter]);

  useEffect(() => {
    if (!allPolygonMetrics || allPolygonMetrics.length === 0) {
      setPolygonMetricsData({});
      return;
    }

    const groupedByStore = groupBy(allPolygonMetrics, 'store.name');

    const processedData: Record<string, any[]> = {};
    
    Object.keys(groupedByStore).forEach((storeName) => {
      processedData[storeName] = groupedByStore[storeName].map((polygon) => ({
        key: polygon.polygon_id,
        id: polygon.polygon_id,
        areas: `${polygon.polygon_name} (${polygon.camera.es_cam_id}: (${polygon.camera.id})`,
        noOfViews: polygon.metrics.unique_track_ids,
        totalTime: convertSecondsToHMS(polygon.metrics.total_time_spent),
        avgTime: convertSecondsToHMS(polygon.metrics.average_time_spent),
      }));

      processedData[storeName] = orderBy(
        processedData[storeName], 
        ['noOfViews'], 
        ['desc']
      );
    });
    
    setPolygonMetricsData(processedData);
  }, [allPolygonMetrics, allPolygonMetricsLoading]);

  const titleLastUpdate = moment(lastUpdate).format('DD/MM/YYYY HH:mm');
  const updateAction = () => {
    const now = moment().subtract(2, 'months').toISOString();
    const weekBeforeLastUpdate = moment(now).subtract(3, 'days').startOf('day').toDate();
    dispatch(ActionTypes.updateLastUpdate(now));
    dispatch(ActionTypes.updateStartDate(weekBeforeLastUpdate));
    dispatch(ActionTypes.updateEndDate(now));
  };
  const lastUpdateAction = (
    <button className="group flex gap-2 cursor-pointer disabled:cursor-not-allowed" type="button" onClick={updateAction} disabled={dwellAnalysisLoading || allPolygonMetricsLoading}>
      <Tooltip tooltipText="Refresh now" tooltipPlacement="top" width={100}>
        <span className=" inline-flex gap-3 text-sm items-center group-disabled:cursor-not-allowed hover:bg-primary/50 transition-all rounded p-2 group-disabled:hover:bg-transparent">
          <span className="group-disabled:text-neutral-400">
            Update
          </span>
          <MdRefresh size={25} className="text-primary-dark/80 group-disabled:text-neutral-400 group-disabled:group-hover:rotate-0 group-hover:rotate-180 transition-all" />
        </span>
      </Tooltip>
    </button>
  );
  return (
    <Layout title="Sky">
      <Row>
        <Col size="12">
          <Card title="Last updates" subTitle={`Last updated at ${titleLastUpdate}`} iconsGroup={lastUpdateAction} />
        </Col>
      </Row>
      <Row>
        <Col size="12">
          <Card>
            {allPolygonMetricsLoading && (
              <div className="animate-pulse">
                <div role="status" className="w-full bg-white rounded-lg flex flex-col shadow gap-2 p-2">
                  <div className="h-6 w-full bg-gray-200 rounded-lg br-5" />
                  <div className="h-20 w-full bg-gray-200 rounded-lg br-5" />
                </div>
              </div>
            )}
            {!allPolygonMetricsLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {Object.keys(polygonMetricsData).length === 0 && (
                  <div className="text-center text-neutral-600 col-span-2">
                    <span>No polygon metrics data available</span>
                  </div>
                )}
                {Object.keys(polygonMetricsData).map((storeName) => (
                  <React.Fragment key={`store-${storeName}`}>
                    {polygonMetricsData[storeName].length === 0 && (
                      <div className="text-center text-neutral-600">
                        <span>
                          No data available for
                          {storeName}
                        </span>
                      </div>
                    )}
                    {polygonMetricsData[storeName].length > 0 && (
                      <Card title={storeName === 'Canary Wharf' ? 'Main Restaurant' : 'Market'}>
                        <Table
                          cols={[
                            { title: 'Areas', field: 'areas', order: true, alignment: 'start' },
                            { title: 'Unique Visitors', field: 'noOfViews', order: true, alignment: 'center' },
                            { title: 'Total Time', field: 'totalTime', order: false, alignment: 'center' },
                            { title: 'Avg. Time', field: 'avgTime', order: false, alignment: 'end' },
                          ]}
                          rows={polygonMetricsData[storeName]}
                        />
                      </Card>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Layout>
  );
}
